import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@rekati/utils";

/*
 * The event in one line: a heartbeat that turns into a data signal and ends in
 * iForte's broadcast mark. "Connecting systems" read left to right: the
 * patient's rhythm (P, QRS, T), handed to a square wave (the hospital's
 * systems), arriving at the signal (the network that carries it).
 *
 * The line draws itself once, then a short bright packet travels it on a loop.
 * Reduced motion: the line is simply there and nothing travels. Decorative:
 * hidden from assistive technology.
 */
const TRACE =
  "M0 60 H300 L312 53 L324 60 H352 L360 70 L374 10 L388 88 L398 60 H432 Q450 38 468 60 H540 V34 H566 V60 H582 V34 H626 V60 H642 V34 H658 V60 H704 V34 H748 V60 H1124";

export function SignalTrace({ className, loop = true }: { className?: string; loop?: boolean }) {
  const reduce = useReducedMotion();
  return (
    <svg
      viewBox="0 0 1200 120"
      fill="none"
      preserveAspectRatio="xMinYMid meet"
      className={cn("w-full text-border", className)}
      aria-hidden="true"
      focusable="false"
    >
      <motion.path
        d={TRACE}
        stroke="currentColor"
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
        initial={{ pathLength: reduce ? 1 : 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2.2, ease: [0.65, 0, 0.35, 1] }}
      />
      {!reduce && loop ? (
        <path
          d={TRACE}
          pathLength={1}
          stroke="var(--color-trace)"
          strokeWidth={3}
          strokeLinejoin="round"
          strokeLinecap="round"
          strokeDasharray="0.07 0.93"
          className="animate-[trace-packet_3.6s_linear_2.2s_infinite] [stroke-dashoffset:1] drop-shadow-[0_0_6px_var(--color-trace)]"
        />
      ) : null}
      <g transform="translate(1146 60)">
        <circle r="7" fill="var(--color-trace)" />
        {[18, 30, 42].map((r, i) => (
          <motion.path
            key={r}
            d={`M ${-r * 0.7} ${-r * 0.7} A ${r} ${r} 0 0 1 ${r * 0.7} ${-r * 0.7}`}
            stroke="var(--color-trace)"
            strokeWidth={4}
            strokeLinecap="round"
            initial={{ opacity: reduce ? 1 : 0 }}
            animate={reduce ? { opacity: 1 } : { opacity: [0, 1, 1, 0.25] }}
            transition={
              reduce
                ? { duration: 0 }
                : {
                    duration: 2.4,
                    times: [0, 0.2, 0.7, 1],
                    delay: 2 + i * 0.18,
                    repeat: loop ? Infinity : 0,
                    repeatDelay: 1.2,
                  }
            }
          />
        ))}
      </g>
    </svg>
  );
}
